import { apiWithkey } from "../axios";
import { baseApiURL, conversacoesApi } from "../endpoints";
import useUserStore from "../../storeModules/useUserStore";
import useConversacaoStore from "../../storeModules/useConversacaoStore";
import useInitialConfigStore from "../../storeModules/useInitialConfigStore";
import DatadogErrorReporter from "../../utils/datadogErrorReporter";
import getMycoins from "../../utils/getMycoins";
import { persistSessionConfig } from "../../utils/sessionConfigStorage";
import getClientDeviceMetadata from "../../utils/getClientDeviceMetadata";

const buildConfigMetadata = () => {
  const { siteDomain, brand, botType, empresaId, desconto, shouldUseCoupon, seguro, referralcode } = useInitialConfigStore.getState();
  return {
    empresa_id: empresaId,
    desconto: !!desconto,
    cupom: !!shouldUseCoupon,
    seguro: !!seguro,
    mycoins: !!getMycoins(),
    referralcode: referralcode || "",
    channel: botType || "bot",
    site_domain: siteDomain || (typeof window !== "undefined" ? window.location.origin : ""),
    brand,
  };
};

const persistConversacaoSession = (data) => {
  if (!data?.id) return;

  localStorage.setItem("mycon_conversacao_id", data.id);

  if (data.leadId) {
    localStorage.setItem("mycon_lead_id", String(data.leadId));
  }

  useConversacaoStore.getState().setConversacaoId(data.id);
};

const getActiveConversacaoId = () => {
  const storeId = useConversacaoStore.getState().conversacaoId;
  const localId =
    typeof window !== "undefined"
      ? localStorage.getItem("mycon_conversacao_id")
      : "";

  return storeId || localId || "";
};

const criarConversacao = async (stage = "boas_vindas") => {
  const url = `${baseApiURL}${conversacoesApi}`;
  const meta = buildConfigMetadata();

  const props = {
    stage,
    channel: meta.channel,
    site_domain: meta.site_domain,
    brand: meta.brand,
    metadata: {
      empresa_id: meta.empresa_id,
      desconto: meta.desconto,
      cupom: meta.cupom,
      seguro: meta.seguro,
      mycoins: meta.mycoins,
      referralcode: meta.referralcode,
      ...getClientDeviceMetadata(),
    },
  };

  return apiWithkey
    .post(url, props)
    .then((response) => {
      const data = response.data;
      useConversacaoStore.getState().setConversacao({
        id: data.id,
        stage: data.stage ?? stage,
        metadata: data.metadata ?? {},
        historico: data.historico ?? [],
      });
      persistConversacaoSession(data);
      persistSessionConfig();
      return data;
    })
    .catch((error) => {
      DatadogErrorReporter.reportApiError(error);
    });
};

const ensureConversacao = async (stage = "boas_vindas") => {
  const existingId = getActiveConversacaoId();
  if (existingId) return existingId;

  const data = await criarConversacao(stage);
  return data?.id ?? "";
};

const startNewConversacaoSession = async (stage = "nova_cota") => {
  localStorage.removeItem("mycon_conversacao_id");
  useConversacaoStore.getState().setConversacao({
    id: "",
    stage: "",
    metadata: {},
    historico: [],
  });
  return criarConversacao(stage);
};

const recuperarConversacao = async (id) => {
  const url = `${baseApiURL}${conversacoesApi}/${id}`;
  return apiWithkey
    .get(url)
    .then((response) => {
      const data = response.data;
      useConversacaoStore.getState().setConversacao({
        id: data.id,
        stage: data.stage,
        metadata: data.metadata ?? {},
        historico: data.historico ?? [],
      });
      persistConversacaoSession(data);
      return data;
    })
    .catch(() => null);
};

const buscarConversacaoPorLead = async (leadId) => {
  const url = `${baseApiURL}${conversacoesApi}/lead/${leadId}`;
  return apiWithkey
    .get(url)
    .then((response) => {
      const data = response.data;
      useConversacaoStore.getState().setConversacao({
        id: data.id,
        stage: data.stage,
        metadata: data.metadata ?? {},
        historico: data.historico ?? [],
      });
      persistConversacaoSession(data);
      return data;
    })
    .catch(() => null);
};

const recuperarMensagens = async (id) => {
  const url = `${baseApiURL}${conversacoesApi}/${id}/mensagens`;
  return apiWithkey
    .get(url)
    .then((response) => {
      const data = response.data;
      const historico = Array.isArray(data) ? data : (data.mensagens ?? data.historico ?? []);
      useConversacaoStore.getState().setConversacao({
        ...useConversacaoStore.getState(),
        historico,
      });
      return historico;
    })
    .catch(() => null);
};

const registrarStep = async (id, stepData = {}) => {
  const url = `${baseApiURL}${conversacoesApi}/${id}/step`;
  return apiWithkey
    .post(url, stepData)
    .then((response) => {
      const data = response.data;
      useConversacaoStore.getState().addHistorico(data);
      if (data.stage) useConversacaoStore.getState().setStage(data.stage);
      return data;
    })
    .catch((error) => {
      DatadogErrorReporter.reportApiError(error);
    });
};

export {
  buildConfigMetadata,
  criarConversacao,
  ensureConversacao,
  getActiveConversacaoId,
  startNewConversacaoSession,
  recuperarConversacao,
  recuperarMensagens,
  buscarConversacaoPorLead,
  registrarStep,
};
